import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { 
  AlertTriangle, 
  CheckCircle2, 
  Clock, 
  TrendingUp,
  MapPin,
  Filter,
  Calendar,
  Loader2,
  Flame,
  Layers
} from 'lucide-react';
import Footer from '../components/Footer';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Coordenadas de Los Alcázares
const MAP_CENTER = [37.7431, -0.8523];

const COLORS_BY_ESTADO = {
  'Nueva/Registrada': '#EF4444',
  'En Proceso': '#F59E0B',
  'Completada': '#10B981'
};

const COLORS_CHART = ['#1E5AA8', '#1F8A70', '#7DB9E8', '#C2410C', '#6F8AA8'];

/**
 * Capa de heatmap usando leaflet.heat.
 * Recibe points = [[lat, lng, intensity], ...] y los pinta sobre el mapa.
 */
function HeatmapLayer({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !Array.isArray(points) || points.length === 0) return undefined;
    const heat = L.heatLayer(points, {
      radius: 28,
      blur: 22,
      maxZoom: 17,
      minOpacity: 0.35,
      gradient: {
        0.2: '#1F8A70',
        0.4: '#F59E0B',
        0.7: '#C2410C',
        1.0: '#B91C1C',
      },
    });
    heat.addTo(map);
    return () => {
      map.removeLayer(heat);
    };
  }, [map, points]);
  return null;
}

export default function Tecnicos() {
  const [incidencias, setIncidencias] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapMode, setMapMode] = useState('both'); // 'markers' | 'heatmap' | 'both'
  const [filtros, setFiltros] = useState({
    estado: '',
    categoria: ''
  });

  useEffect(() => {
    fetchData();
  }, [filtros]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch incidencias
      const params = new URLSearchParams();
      if (filtros.estado) params.append('estado', filtros.estado);
      if (filtros.categoria) params.append('categoria', filtros.categoria);
      params.append('limit', '200');
      
      const [incidenciasRes, statsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/incidencias?${params.toString()}`),
        fetch(`${BACKEND_URL}/api/incidencias/stats`)
      ]);
      
      const incidenciasData = await incidenciasRes.json();
      const statsData = await statsRes.json();
      
      setIncidencias(incidenciasData.features || []);
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Preparar datos para gráficos
  const getCategoriaDistribution = () => {
    const categoriasCount = {};
    incidencias.forEach(inc => {
      const cat = inc.attributes.categoria || 'Sin categoría';
      categoriasCount[cat] = (categoriasCount[cat] || 0) + 1;
    });
    return Object.keys(categoriasCount).map(key => ({
      name: key,
      value: categoriasCount[key]
    }));
  };

  const getEstadoDistribution = () => {
    return [
      { name: 'Nueva', value: stats?.nueva || 0, color: COLORS_BY_ESTADO['Nueva/Registrada'] },
      { name: 'En Proceso', value: stats?.en_proceso || 0, color: COLORS_BY_ESTADO['En Proceso'] },
      { name: 'Completada', value: stats?.completada || 0, color: COLORS_BY_ESTADO['Completada'] }
    ];
  };

  const getMarkerColor = (estado) => {
    return COLORS_BY_ESTADO[estado] || '#6F8AA8';
  };

  // Custom marker icon
  const createCustomIcon = (color) => {
    return L.divIcon({
      className: 'custom-marker',
      html: `<div style="background-color: ${color}; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
      iconSize: [24, 24],
      iconAnchor: [12, 12]
    });
  };

  return (
    <div className="min-h-screen bg-[#FAFCFE]">
      <Helmet>
        <title>Dashboard Técnicos | Cuidemos Los Alcázares®</title>
        <meta name="description" content="Panel de control de incidencias para técnicos municipales" />
      </Helmet>

      {/* Header */}
      <section className="bg-gradient-to-br from-[#0A2540] via-[#1E5AA8] to-[#0A2540] pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <BarChart size={28} className="text-white" />
            </div>
            <span className="text-white/90 text-sm font-medium uppercase tracking-wider">Técnicos Municipales</span>
          </div>
          <h1 className="font-display text-5xl sm:text-6xl text-white mb-5">
            Dashboard de Incidencias
          </h1>
          <p className="text-white/80 text-base sm:text-lg max-w-2xl leading-relaxed">
            Monitoreo en tiempo real de todas las incidencias reportadas en el municipio.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-12">
        
        {/* KPIs */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-12">
            <div className="bg-white rounded-2xl border border-[#CFE3F5] p-6">
              <div className="flex items-center gap-3 mb-3">
                <AlertTriangle size={20} className="text-[#6F8AA8]" />
                <span className="text-sm font-semibold text-[#6F8AA8]">Total</span>
              </div>
              <p className="text-4xl font-display text-[#0A2540] tabular-nums">{stats.total}</p>
              <p className="text-xs text-[#6F8AA8] mt-2">Incidencias registradas</p>
            </div>
            
            <div className="bg-white rounded-2xl border border-[#CFE3F5] p-6">
              <div className="flex items-center gap-3 mb-3">
                <AlertTriangle size={20} className="text-[#EF4444]" />
                <span className="text-sm font-semibold text-[#6F8AA8]">Nuevas</span>
              </div>
              <p className="text-4xl font-display text-[#EF4444] tabular-nums">{stats.nueva}</p>
              <p className="text-xs text-[#6F8AA8] mt-2">Pendientes de atención</p>
            </div>
            
            <div className="bg-white rounded-2xl border border-[#CFE3F5] p-6">
              <div className="flex items-center gap-3 mb-3">
                <Clock size={20} className="text-[#F59E0B]" />
                <span className="text-sm font-semibold text-[#6F8AA8]">En Proceso</span>
              </div>
              <p className="text-4xl font-display text-[#F59E0B] tabular-nums">{stats.en_proceso}</p>
              <p className="text-xs text-[#6F8AA8] mt-2">En gestión activa</p>
            </div>
            
            <div className="bg-white rounded-2xl border border-[#CFE3F5] p-6">
              <div className="flex items-center gap-3 mb-3">
                <CheckCircle2 size={20} className="text-[#10B981]" />
                <span className="text-sm font-semibold text-[#6F8AA8]">Completadas</span>
              </div>
              <p className="text-4xl font-display text-[#10B981] tabular-nums">{stats.completada}</p>
              <p className="text-xs text-[#6F8AA8] mt-2">Resueltas correctamente</p>
            </div>
            
            <div className="bg-white rounded-2xl border border-[#CFE3F5] p-6">
              <div className="flex items-center gap-3 mb-3">
                <TrendingUp size={20} className="text-[#1E5AA8]" />
                <span className="text-sm font-semibold text-[#6F8AA8]">Tiempo Medio</span>
              </div>
              <p className="text-4xl font-display text-[#1E5AA8] tabular-nums">{stats.avg_resolution_time_hours}</p>
              <p className="text-xs text-[#6F8AA8] mt-2">Horas de resolución</p>
            </div>
          </div>
        )}

        {/* Filtros */}
        <div className="bg-white rounded-2xl border border-[#CFE3F5] p-6 mb-12">
          <div className="flex items-center gap-3 mb-4">
            <Filter size={20} className="text-[#1E5AA8]" />
            <h2 className="font-display text-xl text-[#0A2540]">Filtros</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-semibold text-[#0A2540] mb-2">Estado</label>
              <select
                value={filtros.estado}
                onChange={(e) => setFiltros(prev => ({ ...prev, estado: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-[#CFE3F5] focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]"
              >
                <option value="">Todos los estados</option>
                <option value="Nueva/Registrada">Nueva/Registrada</option>
                <option value="En Proceso">En Proceso</option>
                <option value="Completada">Completada</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-[#0A2540] mb-2">Categoría</label>
              <select
                value={filtros.categoria}
                onChange={(e) => setFiltros(prev => ({ ...prev, categoria: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-[#CFE3F5] focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]"
              >
                <option value="">Todas las categorías</option>
                <option value="Limpieza Viaria">Limpieza Viaria</option>
                <option value="Recogida RSU">Recogida RSU</option>
                <option value="Playas y Litoral">Playas y Litoral</option>
                <option value="Parques y Jardines">Parques y Jardines</option>
                <option value="Otros servicios">Otros Servicios</option>
              </select>
            </div>
            
            <div className="sm:col-span-2 flex items-end">
              <button
                onClick={() => setFiltros({ estado: '', categoria: '' })}
                className="px-6 py-2.5 rounded-xl border-2 border-[#CFE3F5] text-[#6F8AA8] hover:bg-[#FAFCFE] transition-colors text-sm font-medium"
              >
                Limpiar filtros
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={40} className="text-[#1E5AA8] animate-spin" />
          </div>
        ) : (
          <>
            {/* Mapa */}
            <div className="bg-white rounded-2xl border border-[#CFE3F5] p-6 mb-12">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <MapPin size={20} className="text-[#1E5AA8]" />
                <h2 className="font-display text-xl text-[#0A2540]">Mapa de Incidencias</h2>
                <span className="text-sm text-[#6F8AA8]">
                  {incidencias.filter(i => i.geometry && i.geometry.x && i.geometry.y).length} geolocalizadas
                </span>
                <div
                  className="ml-auto inline-flex items-center gap-1 p-1 rounded-full bg-[#F5F9FC] border border-[#CFE3F5]"
                  role="tablist"
                  aria-label="Modo de visualización del mapa"
                  data-testid="tecnicos-map-mode-toggle"
                >
                  <MapModeButton
                    active={mapMode === 'markers'}
                    onClick={() => setMapMode('markers')}
                    icon={MapPin}
                    label="Marcadores"
                    testid="tecnicos-map-mode-markers"
                  />
                  <MapModeButton
                    active={mapMode === 'heatmap'}
                    onClick={() => setMapMode('heatmap')}
                    icon={Flame}
                    label="Heatmap"
                    testid="tecnicos-map-mode-heatmap"
                  />
                  <MapModeButton
                    active={mapMode === 'both'}
                    onClick={() => setMapMode('both')}
                    icon={Layers}
                    label="Ambos"
                    testid="tecnicos-map-mode-both"
                  />
                </div>
              </div>
              <div className="h-[500px] rounded-xl overflow-hidden border border-[#CFE3F5]">
                <MapContainer
                  center={MAP_CENTER}
                  zoom={14}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />

                  {(mapMode === 'heatmap' || mapMode === 'both') && (
                    <HeatmapLayer
                      points={incidencias
                        .filter(inc => inc.geometry && inc.geometry.x && inc.geometry.y)
                        .map(inc => {
                          // Ponderamos por prioridad/estado para resaltar zonas críticas
                          const prioridadW = { Urgente: 1.0, Alta: 0.8, Media: 0.5, Baja: 0.3 };
                          const estadoW = {
                            'Nueva/Registrada': 1.0,
                            'En Proceso': 0.6,
                            'Completada': 0.25,
                          };
                          const w = Math.max(
                            prioridadW[inc.attributes.prioridad] ?? 0.5,
                            estadoW[inc.attributes.estado] ?? 0.5,
                          );
                          return [inc.geometry.y, inc.geometry.x, w];
                        })}
                    />
                  )}

                  {(mapMode === 'markers' || mapMode === 'both') &&
                    incidencias
                      .filter(inc => inc.geometry && inc.geometry.x && inc.geometry.y)
                      .map(inc => (
                        <Marker
                          key={inc.attributes.objectid}
                          position={[inc.geometry.y, inc.geometry.x]}
                          icon={createCustomIcon(getMarkerColor(inc.attributes.estado))}
                        >
                          <Popup>
                            <div className="p-2">
                              <h3 className="font-semibold text-[#0A2540] mb-2">
                                Incidencia #{inc.attributes.objectid}
                              </h3>
                              <p className="text-sm text-[#6F8AA8] mb-1">
                                <strong>Estado:</strong> {inc.attributes.estado}
                              </p>
                              <p className="text-sm text-[#6F8AA8] mb-1">
                                <strong>Categoría:</strong> {inc.attributes.categoria}
                              </p>
                              <p className="text-sm text-[#6F8AA8] mb-1">
                                <strong>Dirección:</strong> {inc.attributes.Direccion}
                              </p>
                              <p className="text-sm text-[#6F8AA8]">
                                <strong>Prioridad:</strong> {inc.attributes.prioridad}
                              </p>
                            </div>
                          </Popup>
                        </Marker>
                      ))
                  }
                </MapContainer>
              </div>
              {mapMode !== 'markers' && (
                <div className="mt-3 flex items-center gap-3 text-xs text-[#6F8AA8]">
                  <span className="inline-flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full" style={{ background: '#1F8A70' }} />
                    Baja densidad
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full" style={{ background: '#F59E0B' }} />
                    Media
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full" style={{ background: '#C2410C' }} />
                    Alta
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full" style={{ background: '#B91C1C' }} />
                    Crítica
                  </span>
                  <span className="ml-auto italic">
                    El peso del heatmap combina prioridad y estado de la incidencia.
                  </span>
                </div>
              )}
            </div>

            {/* Gráficos */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
              
              {/* Distribución por Estado */}
              <div className="bg-white rounded-2xl border border-[#CFE3F5] p-6">
                <h3 className="font-display text-lg text-[#0A2540] mb-6">Distribución por Estado</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={getEstadoDistribution()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {getEstadoDistribution().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              {/* Distribución por Categoría */}
              <div className="bg-white rounded-2xl border border-[#CFE3F5] p-6">
                <h3 className="font-display text-lg text-[#0A2540] mb-6">Distribución por Categoría</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={getCategoriaDistribution()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#CFE3F5" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#1E5AA8" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Tabla de Incidencias */}
            <div className="bg-white rounded-2xl border border-[#CFE3F5] p-6">
              <div className="flex items-center gap-3 mb-6">
                <Calendar size={20} className="text-[#1E5AA8]" />
                <h2 className="font-display text-xl text-[#0A2540]">Listado de Incidencias</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#CFE3F5]">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A2540]">ID</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A2540]">Estado</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A2540]">Categoría</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A2540]">Dirección</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A2540]">Prioridad</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A2540]">Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {incidencias.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-[#6F8AA8]">
                          No hay incidencias que mostrar
                        </td>
                      </tr>
                    ) : (
                      incidencias.map(inc => {
                        const attrs = inc.attributes;
                        const fecha = attrs.CreationDate ? new Date(attrs.CreationDate).toLocaleDateString('es-ES') : '-';
                        
                        return (
                          <tr key={attrs.objectid} className="border-b border-[#CFE3F5] hover:bg-[#FAFCFE] transition-colors">
                            <td className="py-4 px-4 text-sm font-mono text-[#0A2540]">#{attrs.objectid}</td>
                            <td className="py-4 px-4 text-sm">
                              <span 
                                className="inline-block px-3 py-1 rounded-full text-xs font-medium"
                                style={{ 
                                  backgroundColor: `${getMarkerColor(attrs.estado)}20`,
                                  color: getMarkerColor(attrs.estado)
                                }}
                              >
                                {attrs.estado}
                              </span>
                            </td>
                            <td className="py-4 px-4 text-sm text-[#6F8AA8]">{attrs.categoria || '-'}</td>
                            <td className="py-4 px-4 text-sm text-[#6F8AA8] max-w-xs truncate">{attrs.Direccion || '-'}</td>
                            <td className="py-4 px-4 text-sm">
                              <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                                attrs.prioridad === 'Urgente' ? 'bg-red-100 text-red-800' :
                                attrs.prioridad === 'Alta' ? 'bg-orange-100 text-orange-800' :
                                attrs.prioridad === 'Media' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {attrs.prioridad || 'Media'}
                              </span>
                            </td>
                            <td className="py-4 px-4 text-sm text-[#6F8AA8]">{fecha}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );

function MapModeButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
        active
          ? 'bg-[#1E5AA8] text-white shadow-sm'
          : 'text-[#0A2540] hover:bg-white'
      }`}
    >
      <Icon size={14} />
      {label}
    </button>
  );
}

}